/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import LinkIcon from '@atlaskit/icon/core/link';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { Expand, ExpandList } from '../../../../../../../../common/ui/expand-list';
import { enrichExcerpt } from '../../../../../../../../common/utils/search-result-utils';
import type { tabs_AtlassianSearchPage$key } from './__generated__/tabs_AtlassianSearchPage.graphql';
import { i18n } from './messages';

const styles = cssMap({
	tabItem: {
		paddingBlock: token('space.050'),
		paddingInlineStart: token('space.100'),
	},
	tabLink: {
		textDecoration: 'none',
	},
});

export const Tabs = ({
	node,
	defaultExpandedIndex,
}: {
	node: tabs_AtlassianSearchPage$key;
	defaultExpandedIndex?: number;
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const data = useFragment(
		graphql`
			fragment tabs_AtlassianSearchPage on SearchResultGraphDocument {
				tabs {
					tabId
					title
					url
				}
			}
		`,
		node,
	);

	const tabs = (data.tabs ?? []).filter((tab): tab is NonNullable<typeof tab> => tab != null);

	if (!tabs.length) {
		return null;
	}

	return (
		<ExpandList defaultExpandedIndex={defaultExpandedIndex}>
			<Expand
				icon={LinkIcon}
				attachmentCount={tabs.length}
				attachmentType="url"
				label={formatMessage(i18n.tabsExpandButtonLabel)}
			>
				<Box>
					{tabs.map((tab, index) => {
						const key = tab.tabId ?? tab.url ?? String(index);
						return (
							<Box key={key} xcss={styles.tabItem}>
								<Inline alignBlock="center" space="space.100">
									<LinkIcon label="" size="medium" color="currentColor" />
									{tab.url ? (
										<Anchor xcss={styles.tabLink} href={tab.url} rel="noreferrer" target="_blank">
											<Text size="medium" color="color.link" weight="medium">
												{enrichExcerpt(tab.title ?? tab.url)}
											</Text>
										</Anchor>
									) : (
										<Text size="medium" weight="medium">
											{enrichExcerpt(tab.title)}
										</Text>
									)}
								</Inline>
							</Box>
						);
					})}
				</Box>
			</Expand>
		</ExpandList>
	);
};
